import { z } from 'zod';

import { IsoDateSchema, PaginatedResponseSchema } from './common.js';

// Slug-style IDs are seeded by hand. The regex prevents spaces, capitals, and
// special characters from sneaking into seed data — a curation-time guard for
// the foods.seed.ts file landing in M4 commit 2.
const FOOD_ID_PATTERN = /^[a-z][a-z0-9_]*$/;

export const FoodIdSchema = z.string().min(1).max(64).regex(FOOD_ID_PATTERN);

// Food's intrinsic dietary classification, used for eligibility filtering
// against the user's onboarding `foodPreference`. Eligibility ladder
// (vegan ⊂ vegetarian ⊂ egg ⊂ pescetarian ⊂ non_vegetarian) is implemented
// in commit 2's foods.service.ts. Pescetarian was added in M4 to close the
// 5th-preference gap that main:lib/ai.ts left open.
export const FoodTypeEnum = z.enum(['vegetarian', 'non_vegetarian', 'pescetarian', 'egg', 'vegan']);
export type FoodType = z.infer<typeof FoodTypeEnum>;

// `snack` covers items that don't fit cleanly into the other 12 categories
// (samosa, chips, popcorn, granola bars). `supplement` covers protein powder,
// creatine, etc. — the 20 supplement rows in the seed. `restaurant` covers
// eaten-out / ordered-in menu items — branded chain rows (McDonald's, KFC …)
// and unbranded outside-food (vada pav, shawarma). See
// docs/catalog/RESTAURANT-CATEGORY.md for the scope boundary (a sealed retail
// packet stays a packaged-food row; a hot counter serving is `restaurant`).
export const FoodCategoryEnum = z.enum([
  'protein',
  'grain',
  'vegetable',
  'fruit',
  'dairy',
  'fat_oil',
  'nut_seed',
  'legume',
  'beverage',
  'sweet',
  'snack',
  'supplement',
  'restaurant',
  'other',
]);
export type FoodCategory = z.infer<typeof FoodCategoryEnum>;

// Market a branded restaurant row's macros come from. The same menu item has
// materially different macros by market (McDonald's US Big Mac vs a hypothetical
// IN one), so branded rows must not collapse across regions. `GLOBAL` is for
// unbranded outside-food with no market-specific value (shawarma, sushi).
// Required whenever `brand` is non-null (enforced in FoodSchema.superRefine).
export const FoodRegionEnum = z.enum(['IN', 'US', 'GLOBAL']);
export type FoodRegion = z.infer<typeof FoodRegionEnum>;

// ADR-0015 unit-type system. Every Food row carries one of three unit modes:
// 'count'   — countable items (banana, egg, roti). Drawer renders +/- buttons
//             plus an optional small/medium/large size selector.
// 'portion' — household-measure items (dal, milk, biryani). Drawer renders a
//             small/medium/large portion picker.
// 'grams'   — direct-weight items (paneer, oats, supplements). Drawer renders
//             the existing grams slider.
export const FoodUnitTypeEnum = z.enum(['count', 'portion', 'grams']);
export type FoodUnitType = z.infer<typeof FoodUnitTypeEnum>;

// Single label set used by both `sizes` (count mode) and the portion picker
// (portion mode). The unitType discriminates how the label is interpreted.
export const FoodSizeLabelEnum = z.enum(['small', 'medium', 'large']);
export type FoodSizeLabel = z.infer<typeof FoodSizeLabelEnum>;

// ADR-0022 — every catalog row points at one of four upstream sources.
// 'ifct_2017'      — IFCT 2017 (NIN/ICMR) food code in sourceCode
// 'usda_fdc'       — USDA FoodData Central, FDC ID in sourceCode
// 'recipe_derived' — composite dish, recipe formula key in sourceCode
// 'manufacturer'   — branded item, brand+SKU in sourceCode
export const FoodSourceRefEnum = z.enum([
  'ifct_2017',
  'usda_fdc',
  'recipe_derived',
  'manufacturer',
]);
export type FoodSourceRef = z.infer<typeof FoodSourceRefEnum>;

// For count foods: gramsPerUnit per size (small banana = 90 g, etc.).
// For portion foods: grams per portion (small bowl of dal = 150 g, etc.).
export const FoodSizesSchema = z.object({
  small: z.number().positive(),
  medium: z.number().positive(),
  large: z.number().positive(),
});
export type FoodSizes = z.infer<typeof FoodSizesSchema>;

// Named household measurement options for grams-mode foods (the "handful of
// almonds" problem — a user knows handfuls/spoons/pieces, not raw grams).
// Each entry maps a friendly English label to its gram weight; the log drawer
// renders these as quick-fill chips that resolve to grams, so no downstream
// macro/diet/review math changes (everything still keys off quantityG).
// Ordered most-common-first; the first entry is the drawer default. Only
// populated on grams-mode foods that have an intuitive household unit; null
// everywhere else (count foods use +/-, portion foods use small/medium/large).
export const FoodMeasureSchema = z.object({
  label: z.string().min(1).max(40),
  grams: z.number().positive().max(2000),
});
export type FoodMeasure = z.infer<typeof FoodMeasureSchema>;

export const FoodSchema = z
  .object({
    id: FoodIdSchema,
    name: z.string().min(1).max(120),
    category: FoodCategoryEnum,
    type: FoodTypeEnum,
    kcalPer100g: z.number().min(0).max(900),
    proteinPer100g: z.number().min(0).max(100),
    carbsPer100g: z.number().min(0).max(100),
    fatPer100g: z.number().min(0).max(100),
    defaultServingG: z.number().positive().max(2000),
    defaultServingLabel: z.string().min(1).max(60),
    unitType: FoodUnitTypeEnum,
    sizes: FoodSizesSchema.nullable(),
    defaultGramsPerUnit: z.number().positive().nullable(),
    // Named household measures (handful, tablespoon, small bowl, piece …).
    // Additive + nullable: independent of the unitType invariants below, a
    // pure logging-UX convenience. Max 5 keeps the drawer chip row scannable.
    measures: z.array(FoodMeasureSchema).min(1).max(5).nullable().default(null),
    // ADR-0022 — provenance. Nullable so existing rows survive prisma db
    // push; the M8 rebuild populates these for every row. Tighten to
    // NOT NULL with a startup-time assertion once every catalog row is
    // sourced.
    sourceRef: FoodSourceRefEnum.nullable(),
    sourceCode: z.string().max(64).nullable(),
    // Restaurant & Takeout category (docs/catalog/RESTAURANT-CATEGORY.md).
    // Both nullable so every existing non-restaurant row survives unchanged.
    // `brand` is the chain name for branded rows, null for unbranded outside-
    // food; it is also folded into `name` so client search matches either.
    // `region` is required whenever `brand` is set (superRefine below).
    brand: z.string().min(1).max(60).nullable().default(null),
    region: FoodRegionEnum.nullable().default(null),
    createdAt: IsoDateSchema,
    updatedAt: IsoDateSchema,
  })
  // Cross-field invariants per ADR-0015. The DB allows looser combinations
  // (no SQL CHECK constraint); Zod enforces at every API boundary so a row
  // failing these rules can never reach the diet pipeline or the drawer.
  .superRefine((food, ctx) => {
    if (food.unitType === 'count') {
      if (food.defaultGramsPerUnit === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['defaultGramsPerUnit'],
          message: "unitType='count' requires defaultGramsPerUnit to be set",
        });
      }
    } else if (food.unitType === 'portion') {
      if (food.sizes === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['sizes'],
          message: "unitType='portion' requires sizes to be set",
        });
      }
      if (food.defaultGramsPerUnit !== null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['defaultGramsPerUnit'],
          message: "unitType='portion' requires defaultGramsPerUnit to be null",
        });
      }
    } else {
      // unitType === 'grams'
      if (food.sizes !== null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['sizes'],
          message: "unitType='grams' requires sizes to be null",
        });
      }
      if (food.defaultGramsPerUnit !== null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['defaultGramsPerUnit'],
          message: "unitType='grams' requires defaultGramsPerUnit to be null",
        });
      }
    }

    // A branded row without a region is unsafe — macros differ by market, so a
    // null region silently mixes them. Region is mandatory once brand is set.
    if (food.brand !== null && food.region === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['region'],
        message: 'a non-null brand requires a non-null region (IN/US/GLOBAL)',
      });
    }
  });
export type Food = z.infer<typeof FoodSchema>;

export const ListFoodsQuerySchema = z.object({
  category: FoodCategoryEnum.optional(),
  type: FoodTypeEnum.optional(),
  search: z.string().trim().min(1).max(100).optional(),
  ids: z
    .preprocess(
      (value) =>
        typeof value === 'string'
          ? value
              .split(',')
              .map((id) => id.trim())
              .filter(Boolean)
          : value,
      z.array(z.string().min(1)).max(100),
    )
    .optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(500).default(50),
});
export type ListFoodsQuery = z.infer<typeof ListFoodsQuerySchema>;

export const ListFoodsResponseSchema = PaginatedResponseSchema(FoodSchema);
export type ListFoodsResponse = z.infer<typeof ListFoodsResponseSchema>;
